import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import * as services from "../../services/saleng";
import toast, { Toaster } from "react-hot-toast";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { green, red } from "@mui/material/colors";
import { formatNumber } from "./../../utils/formatNumber";
import { formatDate } from "../../utils/formatDate";

const Page = () => {
  const { branchId } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowModesModel, setRowModesModel] = useState({});
  const [searchText, setSearchText] = useState("");
  const [priceAnnouncementType, setPriceAnnouncementType] = useState("HEAD_OFFICE");
  const isSelfAnnounce = priceAnnouncementType === "SELF_ANNOUNCE";

  const columns = [
    { field: "no", headerName: "ลำดับ", flex: 0, width: 70 },
    { field: "itemCode", headerName: "รหัสสินค้า", flex: 0, width: 70 },
    { field: "title", headerName: "ชื่อสินค้า", flex: 2 },
    { field: "category", headerName: "หมวดหมู่", flex: 1 },
    { field: "subCategory", headerName: "หมวดหมู่ย่อย", flex: 1 },
    { field: "description", headerName: "คำอธิบาย", flex: 1.5 },
    {
      field: "visible",
      headerName: "แสดงผล",
      width: 120,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const isVisible = params.value;
        const color = isVisible ? green[600] : red[500];
        const Icon = isVisible ? CheckCircleIcon : CancelIcon;

        return (
          <Icon
            style={{ color, cursor: "pointer" }}
            onClick={() => handleToggleVisible(params.row.id, !isVisible)}
          />
        );
      },
    },
    {
      field: "price",
      headerName: "ราคาซื้อทั่วไป",
      type: "number",
      flex: 1.5,
      minWidth: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box display="flex" justifyContent="flex-end" width="100%">
          {formatNumber(params.value ?? 0)}
        </Box>
      ),
    },
    {
      field: "adjustment",
      headerName: "ราคาเพิ่ม/ลด ต่อหน่วย",
      type: "number",
      editable: !isSelfAnnounce,
      flex: 1.5,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const value = params.value ?? 0;
        const color = value < 0 ? "#f44336" : "#4caf50";
        return (
          <Box
            display="flex"
            justifyContent="flex-end"
            width="100%"
            color={color}
          >
            {formatNumber(value)}
          </Box>
        );
      },
    },
    {
      field: "total",
      headerName: "ราคารวม",
      type: "number",
      editable: isSelfAnnounce,
      flex: 1.5,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={0.5}
          width="100%"
        >
          {formatNumber(params.value ?? 0)}
          {isSelfAnnounce && (
            <EditIcon
              fontSize="small"
              sx={{ color: "primary.main", opacity: 0.8 }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "สถานะ",
      flex: 1.5,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const status = params.row.status;

        return status ? (
          <Chip label="ประกาศแล้ว" color="success" />
        ) : (
          <Chip label="รอประกาศ" color="warning" />
        );
      },
    },
    {
      field: "updatedAt",
      headerName: "แก้ไขล่าสุด",
      flex: 2,
      renderCell: (params) => formatDate(params.value),
    },
  ];

  const handleToggleVisible = async (productId, newVisible) => {
    const row = rows.find((r) => r.id === productId);
    if (!row) return;

    const payload = {
      branchId: Number(branchId),
      productId,
      visible: newVisible,
    };

    try {
      await services.updateProductSetting(payload, row.productId);

      setRows((prev) =>
        prev.map((r) =>
          r.id === productId
            ? { ...r, visible: newVisible, settingItem: true }
            : r
        )
      );
      toast.success("อัปเดตการแสดงผลแล้ว");
    } catch (err) {
      toast.error("อัปเดตการแสดงผลล้มเหลว");
    }
  };

  const fetchProducts = useCallback(async () => {
    const [shopRes, res, adjRes, settingRes] = await Promise.all([
      services.getShop(),
      services.getProducts2(branchId),
      services.getProductAdjustments(branchId),
      services.getProductSettings(branchId),
    ]);
    const shop = shopRes.data.data;
    const branch = shop?.branches?.find(
      (item) => Number(item.id) === Number(branchId),
    );
    const announcementType = branch?.priceAnnouncementType || "HEAD_OFFICE";
    setPriceAnnouncementType(announcementType);

    const products = res.data.data;
    const adjustments = adjRes.data.data;
    const settings = settingRes.data.data;
    //  console.log(settings)
    const settingMap = new Map(settings.map((s) => [s.productId, s]));
  
    // ✅ แปลงและ sort ตาม item.code ก่อน map
    const productsWithIndex = products
      .slice() // clone array เผื่อไม่อยาก mutate ตัวเดิม
      .sort((a, b) => {
        const codeA = a.code ?? "";
        const codeB = b.code ?? "";
  
        // ถ้ามีเลขใน code (เช่น "P-10", "P-2") ให้เปรียบเทียบตามตัวเลข
        const numA = parseFloat(codeA.replace(/[^\d.]/g, "")) || 0;
        const numB = parseFloat(codeB.replace(/[^\d.]/g, "")) || 0;
  
        // ถ้าทั้งคู่มีตัวเลขให้เทียบตามตัวเลข
        if (numA !== numB) return numA - numB;
  
        // ถ้าไม่มีเลขหรือเท่ากันให้เทียบตามตัวอักษร
        return codeA.localeCompare(codeB, "th", { numeric: true });
      })
      .map((item, index) => {
        const adjItem = adjustments.find((a) => a.productId === item.product.id);
        console.log(item)

        const setting = settingMap.get(item.productId);
        // console.log(setting)

        const storedAdjustment =
          adjItem?.adjustment2 ?? adjItem?.adjustment ?? 0;
        const manualTotal =
          announcementType === "SELF_ANNOUNCE"
            ? adjItem?.totalPrice2 ?? adjItem?.totalPrice ?? null
            : null;
        const total =
          manualTotal !== null
            ? manualTotal
            : (item.price ?? 0) + storedAdjustment;
        const adjustment =
          manualTotal !== null ? total - (item.price ?? 0) : storedAdjustment;
  
        return {
          ...item,
          no: index + 1,
          itemCode: item.product?.code ?? item.code,
          title: item.product?.title ?? item.title,
          category: item.product?.category?.name ?? item.category,
          subCategory: item.product?.subCategory?.name ?? item.subCategory,
          adjustment,
          total,
          adjItem: !!adjItem,
          visible: setting?.visible ?? true,
          status:
            announcementType === "SELF_ANNOUNCE"
              ? adjItem?.totalPrice2 == null && adjItem?.adjustment2 == null
              : adjItem?.adjustment2 == null,
          updatedAt: adjItem?.updatedAt || item?.updatedAt,
        };
      });
  
    setRows(productsWithIndex);
    setLoading(false);
  }, [branchId]);
  

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const processRowUpdate = async (newRow) => {
    const basePrice = Number(newRow.price) || 0;
    const nextTotal = Number(newRow.total) || 0;
    const nextAdjustment = isSelfAnnounce
      ? nextTotal - basePrice
      : Number(newRow.adjustment) || 0;
    newRow.total = isSelfAnnounce
      ? nextTotal
      : basePrice + nextAdjustment;
    newRow.adjustment = nextAdjustment;

    const payload = {
      productId: newRow.productId,
      adjustment: nextAdjustment,
      ...(isSelfAnnounce ? { totalPrice: nextTotal } : {}),
    };

    try {
      if (newRow.adjItem) {
        await services.updateProductAdjustments(payload, branchId);
      } else {
        await services.createProductAdjustments(payload, branchId);
        payload.adjItem = true;
      }

      toast.success("อัปเดตราคาสำเร็จแล้ว");
      await fetchProducts();
      return { ...newRow, isNew: false };
    } catch (error) {
      toast.error("เกิดข้อผิดพลาดในการอัปเดตราคา");
      throw error;
    }
  };

  const filteredRows = rows.filter((row) => {
    const q = searchText.trim().toLowerCase();
    return (
      row.title?.toLowerCase().includes(q) ||
      row.category?.toLowerCase().includes(q) ||
      row.subCategory?.toLowerCase().includes(q)||
      row.itemCode?.toLowerCase().includes(q)

    );
  });
 console.log(filteredRows)
  return (
    <div>
      <AppBreadcrumbs />
      <Toaster position="bottom-left" reverseOrder={false} />

      {/* Header & Search */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          mt: 2,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography component="h2" variant="h5">
          จัดการราคา
        </Typography>

        <TextField
          fullWidth
          label="ค้นหา"
          placeholder="รหัสสินค้่า/ชื่อสินค้า / หมวดหมู่"
          variant="outlined"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ maxWidth: { xs: "100%", sm: 420 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Paper>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={setRowModesModel}
              processRowUpdate={processRowUpdate}
              isCellEditable={(params) =>
                params.field === "adjustment"
                  ? !isSelfAnnounce
                  : params.field === "total"
                    ? isSelfAnnounce
                    : false
              }
              loading={loading}
              // pageSizeOptions={[25, 50, 100]}
              // initialState={{
              //   pagination: { paginationModel: { page: 0, pageSize: 25 } },
              // }}
            />
          </Paper>
        </Box>
      </Box>
    </div>
  );
};

export default Page;
